import { z } from 'zod';
import { TouchChannelSchema } from './api-attribution.js';
import { AppointmentStatusSchema, CallStatusSchema } from './enums.js';

// Usage metering, budgets/caps and reports (PRD §17). Browser-safe.
//
// Metering vocabulary (shared with the agent runtime and provider adapters):
// - One UsageEvent per measurable consumption. `quantity` is in the meter's unit (see USAGE_METER_INFO).
// - Model tokens are two meters (`model_tokens_input`, `model_tokens_output`) instead of one meter with a
//   direction: providers price them differently and caps are set independently.
// - `measured` = the quantity/cost comes from the provider (invoice-grade); false = our estimate.
// - `billedTo` = who pays the provider: the customer (their Aircall/Meta account) or the platform (Estateflow).
// - Budgets are per calendar month in the workspace timezone. Platform-funded meters always have a hard cap
//   (a code default unless a workspace/project budget overrides it); PRD §17 forbids unlimited by default.

const Id = z.string().min(1).max(64);
const Iso = z.string();
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Usa el formato AAAA-MM-DD');

// ───────────── Meters ─────────────

export const USAGE_METERS = [
  'call_minutes',
  'whatsapp_messages',
  'whatsapp_templates',
  'model_tokens_input',
  'model_tokens_output',
  'transcription_seconds',
  'storage_bytes',
  'embedding_tokens',
  'copilot_tokens_input',
  'copilot_tokens_output',
  'copilot_retrievals',
] as const;
export const UsageMeterSchema = z.enum(USAGE_METERS);
export type UsageMeter = z.infer<typeof UsageMeterSchema>;

export const USAGE_BILLED_TO = ['customer', 'platform'] as const;
export const UsageBilledToSchema = z.enum(USAGE_BILLED_TO);
export type UsageBilledTo = z.infer<typeof UsageBilledToSchema>;

export const USAGE_BILLED_TO_LABELS: Record<UsageBilledTo, string> = { customer: 'Cliente (cuenta propia)', platform: 'Plataforma (Estateflow)' };

export interface UsageMeterInfo {
  label: string;
  /** Unit shown next to quantities. */
  unit: string;
  /** Who normally pays the provider for this meter (PRD §1: customer owns Aircall/Meta assets). */
  defaultBilledTo: UsageBilledTo;
  description: string;
}

export const USAGE_METER_INFO: Record<UsageMeter, UsageMeterInfo> = {
  call_minutes: { label: 'Minutos de llamada', unit: 'min', defaultBilledTo: 'customer', description: 'Duración de llamadas conectadas (cuenta de Aircall del cliente).' },
  whatsapp_messages: { label: 'Mensajes de WhatsApp', unit: 'mensajes', defaultBilledTo: 'customer', description: 'Mensajes de sesión enviados por la plataforma.' },
  whatsapp_templates: { label: 'Plantillas de WhatsApp', unit: 'plantillas', defaultBilledTo: 'customer', description: 'Mensajes de plantilla (cobrados por Meta a la cuenta del cliente cuando es posible).' },
  model_tokens_input: { label: 'Tokens de modelo (entrada)', unit: 'tokens', defaultBilledTo: 'platform', description: 'Tokens enviados al modelo de lenguaje.' },
  model_tokens_output: { label: 'Tokens de modelo (salida)', unit: 'tokens', defaultBilledTo: 'platform', description: 'Tokens generados por el modelo de lenguaje.' },
  transcription_seconds: { label: 'Transcripción', unit: 's', defaultBilledTo: 'platform', description: 'Segundos de audio transcritos.' },
  storage_bytes: { label: 'Almacenamiento subido', unit: 'bytes', defaultBilledTo: 'platform', description: 'Bytes de archivos subidos en el periodo (documentos y materiales).' },
  embedding_tokens: { label: 'Tokens de indexación semántica', unit: 'tokens', defaultBilledTo: 'platform', description: 'Tokens enviados al proveedor de embeddings (estimados por longitud del texto).' },
  copilot_tokens_input: { label: 'Copiloto: tokens de entrada', unit: 'tokens', defaultBilledTo: 'platform', description: 'Tokens enviados al modelo para sugerencias y resúmenes del copiloto de llamadas.' },
  copilot_tokens_output: { label: 'Copiloto: tokens de salida', unit: 'tokens', defaultBilledTo: 'platform', description: 'Tokens generados por el modelo para el copiloto de llamadas.' },
  copilot_retrievals: { label: 'Copiloto: consultas de conocimiento', unit: 'consultas', defaultBilledTo: 'platform', description: 'Búsquedas en documentos y herramientas de datos hechas por el copiloto.' },
};

// ───────────── Budgets ─────────────

export const UsageBudgetStateSchema = z.enum(['ok', 'warning', 'exceeded']);
export type UsageBudgetState = z.infer<typeof UsageBudgetStateSchema>;
export const USAGE_BUDGET_STATE_LABELS: Record<UsageBudgetState, string> = { ok: 'Dentro del límite', warning: 'Cerca del límite', exceeded: 'Límite alcanzado' };

export const UsagePeriodSchema = z.object({
  /** e.g. "2026-09" */
  key: z.string(),
  label: z.string(),
  start: Iso,
  end: Iso,
  timezone: z.string(),
});
export type UsagePeriod = z.infer<typeof UsagePeriodSchema>;

export const UsageBudgetSchema = z.object({
  /** null for a code default (platform-funded meter without a configured budget). */
  id: Id.nullable(),
  source: z.enum(['custom', 'default']),
  meter: UsageMeterSchema,
  projectId: Id.nullable(),
  projectName: z.string().nullable(),
  period: z.literal('month'),
  warnAt: z.number(),
  hardCap: z.number(),
  used: z.number(),
  state: UsageBudgetStateSchema,
  updatedAt: Iso.nullable(),
});
export type UsageBudget = z.infer<typeof UsageBudgetSchema>;

export const UsageBudgetListSchema = z.object({
  period: UsagePeriodSchema,
  items: z.array(UsageBudgetSchema),
});
export type UsageBudgetList = z.infer<typeof UsageBudgetListSchema>;

export const UpsertUsageBudgetSchema = z
  .object({
    meter: UsageMeterSchema,
    projectId: Id.nullable().optional(),
    warnAt: z.number().finite().min(0).max(1e14),
    hardCap: z.number().finite().positive().max(1e14),
  })
  .refine((b) => b.warnAt <= b.hardCap, { message: 'El aviso debe ser menor o igual al límite', path: ['warnAt'] });
export type UpsertUsageBudget = z.infer<typeof UpsertUsageBudgetSchema>;

// ───────────── Usage summary / series ─────────────

export const UsageSummaryQuerySchema = z.object({
  /** Calendar month "AAAA-MM" in the workspace timezone; default = current month. */
  month: z.string().regex(/^\d{4}-\d{2}$/).optional(),
  projectId: Id.optional(),
});

export const UsageCostLineSchema = z.object({
  currency: z.string(),
  billedTo: UsageBilledToSchema,
  measured: z.boolean(),
  amountMinor: z.string().regex(/^-?\d+$/),
});
export type UsageCostLine = z.infer<typeof UsageCostLineSchema>;

export const UsageMeterSummarySchema = z.object({
  meter: UsageMeterSchema,
  quantity: z.number(),
  measuredQuantity: z.number(),
  estimatedQuantity: z.number(),
  customerQuantity: z.number(),
  platformQuantity: z.number(),
  events: z.number().int(),
  /** Events without a cost (provider did not report one and we did not estimate). */
  uncostedEvents: z.number().int(),
  costs: z.array(UsageCostLineSchema),
  /** null = nothing was ever recorded for this meter in this workspace (not the same as 0). */
  lastEventAt: Iso.nullable(),
});
export type UsageMeterSummary = z.infer<typeof UsageMeterSummarySchema>;

export const UsageSummarySchema = z.object({
  period: UsagePeriodSchema,
  workspaceCurrency: z.string(),
  isDemo: z.boolean(),
  meters: z.array(UsageMeterSummarySchema),
  byProject: z.array(z.object({ projectId: Id.nullable(), projectName: z.string().nullable(), meter: UsageMeterSchema, quantity: z.number() })),
});
export type UsageSummary = z.infer<typeof UsageSummarySchema>;

export const UsageSeriesQuerySchema = z.object({
  days: z.coerce.number().int().refine((d) => d === 30 || d === 90, 'Usa 30 o 90 días').default(30),
  meter: UsageMeterSchema.optional(),
  projectId: Id.optional(),
});

export const UsageSeriesSchema = z.object({
  timezone: z.string(),
  from: Day,
  to: Day,
  series: z.array(z.object({ meter: UsageMeterSchema, points: z.array(z.object({ date: Day, quantity: z.number() })) })),
});
export type UsageSeries = z.infer<typeof UsageSeriesSchema>;

// ───────────── Reports ─────────────

/** Sentinel for filtering leads whose source/campaign is unknown. */
export const REPORT_UNKNOWN = '_unknown';
export const REPORT_UNKNOWN_LABEL = 'Desconocido';

export const ReportQuerySchema = z.object({
  /** Inclusive local dates in the workspace timezone. Default: the last 30 days. */
  from: Day.optional(),
  to: Day.optional(),
  projectId: Id.optional(),
  /** LeadInterest.sourceSystem (case-insensitive) or `_unknown`. */
  source: z.string().min(1).max(80).optional(),
  /** LeadInterest.campaignId or `_unknown`. */
  campaign: z.string().min(1).max(120).optional(),
});
export type ReportQuery = z.infer<typeof ReportQuerySchema>;

export const ReportMetricSchema = z.object({
  /** null = no data (never shown as 0). Ratios are 0..1; durations are seconds. */
  value: z.number().nullable(),
  numerator: z.number().nullable(),
  denominator: z.number().nullable(),
  unit: z.enum(['count', 'ratio', 'seconds']),
  /** Plain-Spanish definition shown in "¿Cómo se calcula?". */
  definition: z.string(),
});
export type ReportMetric = z.infer<typeof ReportMetricSchema>;

export const AttributionRowSchema = z.object({
  /** null = unknown (rendered "Desconocido"). */
  source: z.string().nullable(),
  campaign: z.string().nullable(),
  leads: z.number().int(),
  qualified: z.number().int(),
  booked: z.number().int(),
});
export type AttributionRow = z.infer<typeof AttributionRowSchema>;

/** Commercial outcomes by first-touch channel + campaign (spec §08.6 «uso analítico»). */
export const AttributionOutcomeRowSchema = z.object({
  /** null = no touch recorded for the lead (rendered «Desconocido»). */
  channel: TouchChannelSchema.nullable(),
  /** Campaign name or id (or utm_campaign) of the first touch; null = unknown. */
  campaign: z.string().nullable(),
  leads: z.number().int(),
  /** Leads with a real two-way exchange (inbound message or answered call). */
  conversations: z.number().int(),
  /** Leads with an appointment booked (confirmed, completed, no-show, cancelled or rescheduled). */
  appointments: z.number().int(),
  /** Appointments reported as held. null = past appointments exist but none has a reported outcome (unknown ≠ 0). */
  held: z.number().int().nullable(),
  /** Past confirmed appointments still without a reported outcome. */
  heldUnknown: z.number().int(),
});
export type AttributionOutcomeRow = z.infer<typeof AttributionOutcomeRowSchema>;

export const ReportSchema = z.object({
  range: z.object({ from: Day, to: Day, start: Iso, end: Iso, timezone: z.string() }),
  filters: z.object({ projectId: Id.nullable(), source: z.string().nullable(), campaign: z.string().nullable() }),
  isDemo: z.boolean(),
  excludedTestLeads: z.number().int(),
  qualifiedStages: z.array(z.string()),
  funnel: z.object({
    eligibleLeads: ReportMetricSchema,
    cohortContacts: ReportMetricSchema,
    attemptedContacts: ReportMetricSchema,
    reachedContacts: ReportMetricSchema,
    answeredCallContacts: ReportMetricSchema,
    usefulCallContacts: ReportMetricSchema,
    qualifiedInterests: ReportMetricSchema,
    confirmedAppointments: ReportMetricSchema,
    bookingConversion: ReportMetricSchema,
  }),
  responseTimes: z.object({
    firstResponseMedian: ReportMetricSchema,
    unansweredConversations: ReportMetricSchema,
    handoffWaitMedian: ReportMetricSchema,
    openHandoffs: ReportMetricSchema,
  }),
  callOutcomes: z.array(z.object({ status: CallStatusSchema, outcome: z.string().nullable(), calls: z.number().int(), contacts: z.number().int() })),
  appointments: z.object({
    definition: z.string(),
    byStatus: z.array(z.object({ status: AppointmentStatusSchema, count: z.number().int() })),
    /** Per project: a joint appointment (setter §08.5) counts in every project it covers; `joint` says how many were joint. */
    byProject: z.array(z.object({ projectId: Id, projectName: z.string(), count: z.number().int(), confirmed: z.number().int(), joint: z.number().int() })),
    byAdvisor: z.array(z.object({ advisor: z.string().nullable(), count: z.number().int(), confirmed: z.number().int() })),
    /** Distinct joint appointments in the range (counted once in byStatus / byAdvisor). */
    jointAppointments: z.number().int(),
  }),
  attribution: z.object({
    definition: z.string(),
    firstTouch: z.array(AttributionRowSchema),
    latestTouch: z.array(AttributionRowSchema),
    booking: z.array(AttributionRowSchema),
    outcomes: z.array(AttributionOutcomeRowSchema),
    outcomesDefinition: z.string(),
  }),
  failures: z.object({
    failedMessages: ReportMetricSchema,
    failedCalls: ReportMetricSchema,
    deadLetterWebhooks: ReportMetricSchema,
    syncFailures: ReportMetricSchema,
    failedIngress: ReportMetricSchema,
  }),
  filterOptions: z.object({ sources: z.array(z.string()), campaigns: z.array(z.string()) }),
});
export type Report = z.infer<typeof ReportSchema>;

export const REPORT_TABLES = ['funnel', 'call_outcomes', 'appointments', 'attribution', 'failures'] as const;
export const ReportTableSchema = z.enum(REPORT_TABLES);
export type ReportTable = z.infer<typeof ReportTableSchema>;
export const REPORT_TABLE_LABELS: Record<ReportTable, string> = {
  funnel: 'Embudo',
  call_outcomes: 'Resultados de llamadas',
  appointments: 'Citas',
  attribution: 'Atribución',
  failures: 'Fallas',
};

export const ReportExportQuerySchema = ReportQuerySchema.extend({ table: ReportTableSchema });

export const ReportSettingsSchema = z.object({
  /** LeadInterest stages counted as "qualified" in reports. Default ['qualified']. */
  qualifiedStages: z.array(z.string().min(1).max(80)).min(1).max(20),
});
export type ReportSettings = z.infer<typeof ReportSettingsSchema>;
export const DEFAULT_QUALIFIED_STAGES = ['qualified'] as const;
